import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Download, FileText, GraduationCap, CheckCircle2, ArrowLeft } from "lucide-react";

const HANDBOOK_URL = "/api/media/ser-africa/docs/student-association-formation-handbook.pdf";
const COVER = "/api/media/ser-africa/uploads/student-group-0bea8a69.jpg";
const TEAM = "/api/media/ser-africa/uploads/ser-team-hakuna-1a4dc42b.jpg";


const emptyRef = { name: "", organisation: "", email: "", phone: "" };
const emptyForm = {
  full_name: "", email: "", phone: "", university: "", department: "", year_or_position: "",
  motivation: "", interest_reason: "", ser_member: "No", attended_events: "",
  prior_experience: "", current_projects: "", mentor_details: "",
  time_commitment: "Yes", long_term: "Yes",
  references: [ { ...emptyRef }, { ...emptyRef }, { ...emptyRef } ],
};


export default function StudentChaptersPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setRef = (idx, k, v) => {
    const refs = form.references.map((r, i) => (i === idx ? { ...r, [k]: v } : r));
    setForm({ ...form, references: refs });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // strip fully-empty reference rows so the schema stays clean
      const references = form.references.filter((r) => r.name.trim() || r.email.trim());
      const { data } = await api.post("/student-chapters/apply", { ...form, references });
      setSuccess(data);
      setForm(emptyForm);
      toast.success("Application submitted!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="student-chapters-page">
      {/* Hero */}
      <section
        className="relative"
        style={{ backgroundColor: "var(--ser-primary-dark)", color: "white" }}
      >
        <div className="absolute inset-0 opacity-30">
          <img src={COVER} alt="" className="w-full h-full object-cover" aria-hidden />
        </div>
        <div className="ser-container max-w-4xl relative py-20 md:py-24">
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-6"
            style={{ color: "rgba(255,255,255,0.85)" }}
            data-testid="back-to-membership"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Membership
          </Link>
          <p
            className="text-[11px] font-bold uppercase tracking-wider mb-4 flex items-center gap-2"
            style={{ color: "var(--ser-clay)" }}
          >
            <GraduationCap className="w-4 h-4" /> Student Chapters in Africa
          </p>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-tight mb-4">
            SER Africa Student Chapters
          </h1>
          <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.85)" }}>
            Building the next generation of restoration practitioners across the continent.
          </p>
        </div>
      </section>

      {/* Intro copy */}
      <section className="ser-section">
        <div className="ser-container max-w-3xl space-y-5">
          <p className="leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
            SER Africa Student Chapters connect students passionate about ecological restoration
            with peers, researchers, practitioners, and professionals across Africa. Student-led
            chapters provide opportunities to learn, collaborate, participate in restoration
            activities, build professional networks, and gain practical experience.
          </p>
          <p className="leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
            Chapters can be established at accredited higher-education institutions by motivated
            student groups, with support from their host institution and guidance from SER Africa.
            Through this initiative, SER Africa is building a strong network of emerging restoration
            professionals and empowering students to contribute to the restoration of Africa's
            ecosystems.
          </p>
          <p className="leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
            <strong style={{ color: "var(--ser-teal)" }}>Interested in starting or joining a Student Chapter?</strong>{" "}
            Explore the requirements and find out how you can get involved.
          </p>
        </div>
      </section>

      {/* Second image */}
      <section className="ser-section pt-0">
        <div className="ser-container max-w-5xl">
          <div className="ser-card overflow-hidden">
            <img src={TEAM} alt="SER Africa team" className="w-full h-auto" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-section-alt)" }}
        data-testid="student-chapters-requirements"
      >
        <div className="ser-container max-w-3xl">
          <p className="eyebrow mb-3">Requirements</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl mb-6"
            style={{ color: "var(--ser-primary-dark)" }}
          >
            Read the Student Association Formation Handbook
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--ser-text-muted)" }}>
            Read the Student Association Formation Handbook for information on benefits,
            expectations, requirements, and next steps.
          </p>
          <a
            href={HANDBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ser-primary inline-flex items-center gap-2"
            data-testid="download-handbook-btn"
          >
            <Download className="w-4 h-4" /> Download Handbook (PDF)
          </a>
          <p className="text-xs mt-6" style={{ color: "var(--ser-text-muted)" }}>
            Once you've reviewed the handbook, apply below.
          </p>
        </div>
      </section>

      {/* Application form */}
      <section className="ser-section" data-testid="student-chapters-apply">
        <div className="ser-container max-w-3xl">
          <p className="eyebrow mb-3">Apply</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl mb-2"
            style={{ color: "var(--ser-primary-dark)" }}
          >
            SERAF Student Chapter Volunteer Screening Form
          </h2>
          <p className="text-sm mb-8" style={{ color: "var(--ser-text-muted)" }}>
            Purpose: to identify committed, capable, and passionate individuals who can lead and
            sustain a SERAF Student Chapter in Kenya.
          </p>

          {success ? (
            <div className="ser-card p-8 text-center" data-testid="student-chapters-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--ser-primary)" }} />
              <h3 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ser-teal)" }}>
                Application received
              </h3>
              <p style={{ color: "var(--ser-text-muted)" }}>{success.message}</p>
              <p className="text-xs mt-3" style={{ color: "var(--ser-text-muted)" }}>
                Reference: <code>{success.application_id}</code>
              </p>
              <button
                onClick={() => setSuccess(null)}
                className="btn-ser-outline mt-6"
                data-testid="submit-another-application"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="ser-card p-6 md:p-8 space-y-6" data-testid="student-chapters-form">
              <SectionHeading icon={<FileText className="w-4 h-4" />} label="Personal information" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="sc-fullname" />
                <Field label="Email address" required type="email" value={form.email} onChange={(v) => set("email", v.toLowerCase())} testid="sc-email" />
                <Field label="Phone number" required value={form.phone} onChange={(v) => set("phone", v)} testid="sc-phone" />
                <Field label="University affiliation" required value={form.university} onChange={(v) => set("university", v)} testid="sc-university" />
                <Field label="Department / faculty" value={form.department} onChange={(v) => set("department", v)} testid="sc-department" />
                <Field label="Current year of study or position" placeholder="e.g. MSc student, Lecturer" value={form.year_or_position} onChange={(v) => set("year_or_position", v)} testid="sc-year" />
              </div>

              <SectionHeading icon={<FileText className="w-4 h-4" />} label="Motivation and commitment" />
              <TextArea label="What motivates you to be involved in ecological restoration work?" required rows={3} value={form.motivation} onChange={(v) => set("motivation", v)} testid="sc-motivation" />
              <TextArea label="Why are you interested in helping to establish a SERAF Student Chapter?" required rows={3} value={form.interest_reason} onChange={(v) => set("interest_reason", v)} testid="sc-interest" />
              <RadioRow label="Are you currently a member of the Society for Ecological Restoration (SER)?" options={["Yes", "No", "Planning to join"]} value={form.ser_member} onChange={(v) => set("ser_member", v)} testid="sc-sermember" />
              <TextArea label="Have you attended any SER-related conferences or events? If yes, please specify." rows={2} value={form.attended_events} onChange={(v) => set("attended_events", v)} testid="sc-attended" />

              <SectionHeading icon={<FileText className="w-4 h-4" />} label="Experience and leadership" />
              <TextArea label="Have you previously led or participated in any student organisations or environmental initiatives? If yes, please describe briefly." rows={3} value={form.prior_experience} onChange={(v) => set("prior_experience", v)} testid="sc-experience" />
              <TextArea label="Are you currently involved in any ecological restoration or related research/projects? If yes, please describe." rows={3} value={form.current_projects} onChange={(v) => set("current_projects", v)} testid="sc-projects" />
              <TextArea label="Do you have a professor or mentor who could support your involvement in the chapter? If yes, please provide their name and contact." rows={2} value={form.mentor_details} onChange={(v) => set("mentor_details", v)} testid="sc-mentor" />

              <SectionHeading icon={<FileText className="w-4 h-4" />} label="Availability and long-term engagement" />
              <RadioRow label="Are you willing to commit time regularly to chapter activities, meetings, and leadership responsibilities?" options={["Yes", "No", "Depends on academic schedule"]} value={form.time_commitment} onChange={(v) => set("time_commitment", v)} testid="sc-commitment" />
              <RadioRow label="Do you see yourself being involved in this chapter for at least one academic year or longer?" options={["Yes", "No", "Not sure"]} value={form.long_term} onChange={(v) => set("long_term", v)} testid="sc-longterm" />

              <SectionHeading icon={<FileText className="w-4 h-4" />} label="References" />
              <p className="text-xs -mt-3" style={{ color: "var(--ser-text-muted)" }}>
                Please provide the names and contact details of at least three people (e.g.
                professor, supervisor) who can provide a reference/testimonial.
              </p>
              {form.references.map((r, i) => (
                <div key={i} className="p-4 rounded" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }} data-testid={`sc-ref-${i}`}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--ser-teal)" }}>Reference {i + 1}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Name" value={r.name} onChange={(v) => setRef(i, "name", v)} />
                    <Field label="Organisation / role" value={r.organisation} onChange={(v) => setRef(i, "organisation", v)} />
                    <Field label="Email" type="email" value={r.email} onChange={(v) => setRef(i, "email", v)} />
                    <Field label="Phone" value={r.phone} onChange={(v) => setRef(i, "phone", v)} />
                  </div>
                </div>
              ))}

              <button
                type="submit"
                disabled={submitting}
                className="btn-ser-primary w-full sm:w-auto"
                data-testid="submit-student-chapter-btn"
              >
                {submitting ? "Submitting…" : "Submit application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}


function SectionHeading({ icon, label }) {
  return (
    <div
      className="flex items-center gap-2 pt-4 border-t"
      style={{ borderColor: "var(--ser-border, #e5e5e5)" }}
    >
      <span style={{ color: "var(--ser-primary)" }}>{icon}</span>
      <p
        className="text-sm font-bold uppercase tracking-wider"
        style={{ color: "var(--ser-teal)" }}
      >
        {label}
      </p>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="ser-label">{label}{required ? " *" : ""}</label>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        className="ser-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, required, rows = 3, testid }) {
  return (
    <div>
      <label className="ser-label">{label}{required ? " *" : ""}</label>
      <textarea
        required={required}
        rows={rows}
        className="ser-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function RadioRow({ label, options, value, onChange, testid }) {
  return (
    <div>
      <label className="ser-label">{label}</label>
      <div className="flex flex-wrap gap-4 mt-1" data-testid={testid}>
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name={testid}
              value={opt}
              checked={value === opt}
              onChange={(e) => onChange(e.target.value)}
            />
            <span style={{ color: "var(--ser-text-muted)" }}>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
